import { Injectable } from '@angular/core';
import { QueryEntity, transaction } from '@datorama/akita';
import { compareEntities, isEquals } from '@lycoris-nubila/ngx-lycoris';
import { first, isNil, reject, uniqBy } from 'lodash-es';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

import { UserModuleRepositoryPort } from 'user/port/module/user-module-repository.port';
import { UserModule } from 'user/user-module';

import { CacheObservable } from '../../../app-decorators';
import { UserModuleState, UserModuleStore } from './user-module.store';

@Injectable()
export class UserModuleRepositoryAdapter
	extends QueryEntity<UserModuleState>
	implements UserModuleRepositoryPort
{
	constructor(protected store: UserModuleStore) {
		super(store);
	}

	public async addUserModule(userModule: UserModule): Promise<void> {
		this.store.upsertMany([this.store.asDocument(userModule)]);
	}

	public findByUserIdAndModuleId(userId: string, moduleId: string): UserModule {
		const documents = this.getAll({
			filterBy: (entity) =>
				isEquals(entity.userId, userId) && isEquals(entity.moduleId, moduleId),
		});

		return this.store.asEntity(first(documents));
	}

	@CacheObservable()
	public observeUserModulesByUserId(userId: string): Observable<UserModule[]> {
		return this.selectAll({
			filterBy: (entity) => isEquals(entity.userId, userId),
		}).pipe(
			map((documents) =>
				documents.map((document) => this.store.asEntity(document))
			),
			distinctUntilChanged(compareEntities),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public async removeUserModule(
		userId: string,
		moduleId: string
	): Promise<void> {
		this.store.remove(
			(entity) =>
				isEquals(entity.userId, userId) && isEquals(entity.moduleId, moduleId)
		);
	}

	@transaction()
	public async saveUserModules(
		entities: UserModule[],
		resetForUserId?: string
	): Promise<void> {
		const usersModules = uniqBy(reject<UserModule>(entities, isNil), 'id');

		if (resetForUserId) {
			this.store.remove((doc) => doc.userId === resetForUserId);
		}

		this.store.upsertMany(usersModules.map((e) => this.store.asDocument(e)));
	}
}
